/**
 * Sweden's trains about the player, live (`/rt/trains`, see
 * realtime/trains.ts and `core/rail/live.ts`): asked for every ten seconds
 * in a box some 20 km each way, as Trafikverket's positions come.
 *
 * Outside Sweden there are none to ask for, and when the Worker does not
 * answer (no Worker running, or its upstream down) none come: either way
 * `live` is false and the game runs its made-up trains instead
 * (`three/trains.ts`).
 */

import type { LiveTrain } from '../core/rail/live.js';
import { readTrains } from '../core/rail/live.js';

/** Sweden, roughly. */
const SWEDEN = { south: 55.2, north: 69.2, west: 10.8, east: 24.3 };
const POLL_MS = 10_000;
/** Half the box asked for, degrees: about 20 km north and south, and east and west at these latitudes. */
const HALF_LAT = 0.18;
const HALF_LON = 0.36;
/** Failed this many times running, the Worker is taken not to have trains this session. */
const GIVE_UP = 3;

export class TrainFeed {
  trains: LiveTrain[] = [];
  /** Live trains are the ones running here: in Sweden, with the Worker answering. */
  live = false;
  private last = -Infinity;
  private fetching = false;
  private failures = 0;

  constructor(private readonly url = '/rt/trains') {}

  update(now: number, lat: number, lon: number): void {
    const inSweden = lat > SWEDEN.south && lat < SWEDEN.north && lon > SWEDEN.west && lon < SWEDEN.east;
    if (!inSweden || this.failures >= GIVE_UP) {
      this.live = false;
      this.trains = [];
      return;
    }
    if (this.fetching || now - this.last < POLL_MS) return;
    this.fetching = true;
    this.last = now;
    const box = { south: lat - HALF_LAT, west: lon - HALF_LON, north: lat + HALF_LAT, east: lon + HALF_LON };
    const query = Object.entries(box).map(([k, v]) => `${k}=${v.toFixed(3)}`).join('&');
    fetch(`${this.url}?${query}`)
      .then(async (response) => {
        if (!response.ok) throw new Error(String(response.status));
        this.trains = readTrains(await response.json());
        this.live = true;
        this.failures = 0;
      })
      .catch(() => {
        this.failures++;
        if (this.failures >= GIVE_UP) { this.live = false; this.trains = []; }
      })
      .finally(() => { this.fetching = false; });
  }
}
